/* eslint-disable camelcase */
import React, {
  Component,
  Fragment,
  Suspense,
  createRef,
  lazy
} from 'react';
import { createPortal } from 'react-dom';
import * as ReactRedux from 'react-redux';

import {
  addParagraphHighlight,
  capitalize,
  commentCarouselEnabled,
  getFeedbackAreaCategories,
  hasNoCommentCategories,
  matchTimestamp,
  mergeParagraphAnnotations,
  resolveCategory,
  snakeToPascalCase
} from 'annotator/annotator-helpers';
import {
  Annotation, AnnotatorObject,
  BellmereBusinessRules,
  BellmereResults,
  CannedReferralData,
  CategoryClassName,
  ClientData,
  CommentCategory,
  CommentHelperSortType,
  CommentHelpersSummary,
  CommentInputAnnotation,
  DisplayType,
  FeedbackData,
  TutorData,
  UserTypes
} from 'annotator/annotator-types';

import CommentEditingHelper from 'annotator/comment-editing-helper';
import CommentInput, { YambaCommentInput } from 'annotator/comment-input';
import CommentNavigationBar from 'annotator/comment-navigation-bar';
import { CommentListContextProvider } from 'annotator/comment-list-context';
import RequiredNotCitedPanel from 'annotator/required-not-cited-panel';
import scrollToHighlight from 'annotator/scroll-to-highlight-helper';

import { AnnotationResult, CriticalThinkingResult } from 'bellmere/types.d';
import findWordBoundaries from 'bellmere/validations/document/findWordBoundaries';
import sentenceLookup from 'bellmere/validations/document/sentenceLookup';
import { ReasonCollection } from 'src/react_components/wombat/quality-assurance/interactions/mentoring-panel-types';
import { BellmereQA } from './bellmere-qa/qa-types.d';
import TaskAlertMessages from './task-alert-messages';

// New tabbed summaries/highlights UI components
const SpecialistFeedbackTabs = lazy(() => import('./specialist-feedback-tabs'));
const CollapsibleSpecialistFeedbackSummaries = lazy(() => import('./collapsible-specialist-feedback-summaries'));
const FeedbackCategoryFilter = lazy(() => import('./feedback-category-filter'));
const QAHeader = lazy(() => import('./bellmere-qa/qa-header'));
const QAFooter = lazy(() => import('./bellmere-qa/qa-footer'));
const SubmissionFeedbackPortal = lazy(() => import('submission-feedback/submission-feedback-portal'));
const FlagForQAPortal = lazy(() => import('./bellmere-qa/flag-for-qa-portal'));
const CommentListChipFilter = lazy(() => import('./comment-list-chip-filter'));
const CommentEmptyState = lazy(() => import('annotator/comment-empty-state'));
const BulkDeletion = lazy(() => import('annotator/bulk-deletion'));
const CitationFilter = lazy(() => import('annotator/citation-filter'));
const HighlightEmptyState = lazy(() => import('annotator/highlight-empty-state'));

declare const Annotator: AnnotatorObject;
declare const ResizeSensor;
declare const renderYambaApp: () => void;

const getCSRFToken = (): string => document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';

const matchesTaskRequirement = (annotation: Annotation): boolean => (
  annotation.commentCategory === CommentCategory.TaskAndContent ||
  (annotation.commentCategory === CommentCategory.CriticalThinking && !!annotation.taskAligned)
);

type CommentListProps = {
  annotations: Annotation[],
  highlightOrder: string[],
  isEditable: boolean,
  showTabbedFeedbackLayout: boolean,
  requiredNotCitedPanelContainer: HTMLDivElement,
  inYambaSession?: boolean,
  inYambaFlow?: boolean,
  questionHighlights: string[],
  tutor: TutorData,
  feedback: FeedbackData,
  cannedReferrals: CannedReferralData[],
  client: ClientData | null,
  userType: string,
  bellmereResults?: BellmereResults,
  bellmereRules?: BellmereBusinessRules,
  persistBellmereResults?: boolean,
  viewingAsBellmereQa?: boolean,
  bellmereQAHeaderData?: BellmereQA.HeaderData
  bellmereQAResults?: BellmereQA.Results,
  bellmereQAReasons?: ReasonCollection,
  bellmereQAEditable?: boolean,
  taskAlertMessages?: string[]
}

type CommentListState = {
  annotations: Annotation[],
  highlightOrder: string[],
  editingAnnotation: string | null,
  postSaveEditRequiredTimestamp: string | null,
  savingSubmission: boolean,
  pendingCompletions: Set<string>,
  selectedAnnotation: string | null,
  highlightSelectedAnnotation: boolean,
  selectedParagraphIndex: string | null,
  displayCitations: boolean,
  markedForDeletionTimestamps: string[],
  bulkDeleteMode: boolean,
  confirmingDeletion: boolean,
  lastDeleteData: null | { highlights: string, timestamp: string },
  activeTab: AnnotatorTab,
  commentHelperCategory: null | CommentCategory | CommentCategory[],
  width: null | number,
  bellmereResults: BellmereResults,
  bellmereQAResults: BellmereQA.Results,
  bellmereQAEditable: boolean,
  commentHelperSortType: CommentHelperSortType,
  noCommentCategories: boolean
}

type AnnotatorTab = null | 'specialistHighlights' | 'citationAssist'

type AnnotationData = {
  submission_annotation: Partial<Annotation>,
  submission?: {
    lock_version: number,
    highlights: string
  },
  other_annotations?: unknown[]
}

class CommentList extends Component<CommentListProps, CommentListState> {
  static instance?: CommentList;

  resizeObserver?: ResizeObserver;

  windowResizeCleanup?: () => void;

  mutationObserverCleanup?: () => void;

  // Guards against overlapping toggleAnnotationCompletion PATCH requests settling out of order.
  pendingCompletionToggles = new Set<string>();

  state = {
    annotations: mergeParagraphAnnotations(this.props.annotations),
    highlightOrder: this.props.highlightOrder,
    postSaveEditRequiredTimestamp: null,
    editingAnnotation: null,
    savingSubmission: false,
    // Render-only mirror of pendingCompletionToggles; mutating a Set on the instance can't trigger a re-render.
    pendingCompletions: new Set<string>(),
    // This is hacked into from _highlights_select.coffee so it is set that way even though not set internally
    selectedAnnotation: null, // eslint-disable-line react/no-unused-state
    highlightSelectedAnnotation: true,
    selectedParagraphIndex: null, // eslint-disable-line react/no-unused-state
    displayCitations: true,
    markedForDeletionTimestamps: [],
    bulkDeleteMode: false,
    confirmingDeletion: false,
    lastDeleteData: null,
    activeTab: null,
    commentHelperCategory: null,
    width: null,
    bellmereResults: this.props.bellmereResults,
    bellmereQAResults: this.props.bellmereQAResults,
    bellmereQAEditable: this.props.bellmereQAEditable,
    commentHelperSortType: 'document-order' as CommentHelperSortType,
    noCommentCategories: hasNoCommentCategories(this.props.feedback, this.props.annotations),
    feedbackAreaCategories: getFeedbackAreaCategories(this.props.feedback)
  };

  node = createRef<HTMLDivElement>();

  commentListNode = createRef<HTMLDivElement>();

  componentDidMount(): void {
    const { annotations } = this.state;
    const activeTab = null;

    // Store instance for external access
    CommentList.instance = this;
    (window as unknown as { CommentListInstance?: CommentList }).CommentListInstance = this;

    document.querySelector('.actual-submission-document').classList.toggle(
      'paragraph-annotations',
      this.addParagraphCSSClass()
    );

    this.setState({ activeTab });
    this.addBellmereResults();
    this.addCommentCategoriesClassToHighlights(annotations);
    const sortedAnnotations = [...document.querySelectorAll('.paragraph-highlight, [data-highlighted]')].
      map((element: HTMLElement) => element.dataset.timestamp);

    this.setState({
      highlightOrder: [...new Set(sortedAnnotations)]
    });

    // Setup width measurement with better React 18 compatibility
    this.setupWidthMeasurement();

    // This records when the comment list has finished loading so we can write specs around load times
    Annotator.loadTimerEnd = new Date();

    // Check if review is finished after component mounts
    if (typeof Annotator.checkIfReviewFinished === 'function') {
      Annotator.checkIfReviewFinished();
    }
  }

  componentDidUpdate(_prevProps: CommentListProps, prevState: CommentListState): void {
    const {
      annotations, editingAnnotation, bulkDeleteMode, selectedAnnotation
    } = this.state;
    const { userType, inYambaSession } = this.props;

    this.addCommentCategoriesClassToHighlights(annotations);

    if (prevState.selectedAnnotation !== selectedAnnotation &&
        commentCarouselEnabled(userType, Annotator.data.product, inYambaSession)) {
      this.scrollActiveCardIntoView();
    }

    if (prevState.editingAnnotation !== editingAnnotation) {
      Annotator.enableHighlighter(!editingAnnotation);
      return;
    }

    if (prevState.bulkDeleteMode !== bulkDeleteMode) {
      Annotator.enableHighlighter(!bulkDeleteMode);
    }
  }

  componentWillUnmount(): void {
    // Clean up ResizeSensor
    const sidebarEl = this.sidebarElement();
    if (sidebarEl) {
      ResizeSensor.detach(sidebarEl);
    }

    // Clean up ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }

    // Clean up window resize listener
    if (this.windowResizeCleanup) {
      this.windowResizeCleanup();
      this.windowResizeCleanup = undefined;
    }

    // Clean up mutation observer
    if (this.mutationObserverCleanup) {
      this.mutationObserverCleanup();
      this.mutationObserverCleanup = undefined;
    }

    // Clear instance reference
    if (CommentList.instance === this) {
      CommentList.instance = undefined;
    }
    const windowWithInstance = window as unknown as { CommentListInstance?: CommentList };
    if (windowWithInstance.CommentListInstance === this) {
      windowWithInstance.CommentListInstance = undefined;
    }
  }

  // Deck cards only (.outer-panel-active) — everyone else still scrolls via _highlights_select.coffee.
  scrollActiveCardIntoView = (): void => {
    const activeCard = this.commentListNode.current?.querySelector('.outer-panel-active');

    activeCard?.scrollIntoView({ block: 'center', inline: 'nearest' });
  };

  addParagraphCSSClass = (): boolean => {
    const { annotations, bellmereResults } = this.state;

    const hasCriticalThinkingResults = !!(bellmereResults?.criticalThinkingResults?.length);
    const hasBellmereParagraphAnnotations =
      !!(bellmereResults?.annotationResults?.some((annotation) => annotation.displayType === DisplayType.Paragraph));
    const hasParagraphAnnotations =
      !!(annotations?.some((annotation) => annotation.display_type === DisplayType.Paragraph));

    return hasCriticalThinkingResults || hasBellmereParagraphAnnotations || hasParagraphAnnotations;
  };

  // Because of the JS scrollbar we need to attach the resize sensor "outside" of the component
  sidebarElement = (): HTMLElement => {
    const element = $(this.node.current).closest('.annotator-submission-sidebar')[0];
    return element;
  };

  checkWidth = (): void => {
    const $el = $(this.node.current);
    const width = $el.outerWidth();

    // Only update if width is valid and different from current state
    if (width && width > 0 && width !== this.state.width) {
      const shouldBeVertical = width < 220;
      this.setState({ width });

      // Apply the CSS class to the correct comment-list element
      const $commentListEl = $(this.commentListNode.current);
      $commentListEl.toggleClass('comment-list-delete-reasons-vertical', shouldBeVertical);
    }
  };

  waitForStableLayout = (callback: () => void): void => {
    if (!this.node.current) {
      setTimeout(() => {
        this.waitForStableLayout(callback);
      }, 50);
      return;
    }

    let previousWidth = 0;
    let stableCount = 0;
    const maxAttempts = 30; // Increase max attempts
    let attempts = 0;

    // Set a longer timeout fallback to allow for content loading
    const timeoutFallback = setTimeout(() => {
      callback();
    }, 2000); // 2 second timeout to allow for content loading

    const checkStability = () => {
      attempts += 1;
      const currentWidth = $(this.node.current).outerWidth();

      if (currentWidth === previousWidth && currentWidth > 0) {
        stableCount += 1;
        if (stableCount >= 5) {
          // Width has been stable for 5 checks, proceed with callback
          clearTimeout(timeoutFallback);
          callback();
          return;
        }
      } else {
        stableCount = 0;
      }

      previousWidth = currentWidth;

      if (attempts < maxAttempts) {
        requestAnimationFrame(checkStability);
      } else {
        // Fallback: just execute callback with current state
        clearTimeout(timeoutFallback);
        callback();
      }
    };

    // Wait for window load event + additional delay for layout to settle
    const startCheck = () => {
      requestAnimationFrame(checkStability);
    };

    if (document.readyState === 'complete') {
      // Page already loaded, wait a bit for layout to settle
      setTimeout(startCheck, 200);
    } else {
      // Wait for page load, then additional delay
      window.addEventListener('load', () => {
        setTimeout(startCheck, 200);
      }, { once: true });
    }
  };

  setupWidthMeasurement = (): void => {
    // React 18 compatible width measurement setup
    const measureWidth = () => {
      this.checkWidth();
    };

    // Try to find sidebar element for ResizeSensor
    const sidebarEl = this.sidebarElement();
    if (sidebarEl) {
      ResizeSensor(sidebarEl, this.checkWidth);

      // Initial measurement - wait for layout stability
      this.waitForStableLayout(() => this.checkWidth());
    } else {
      // Use ResizeObserver if available (React 18 friendly)
      if (window.ResizeObserver && this.node.current) {
        let isInitialMeasurement = true;
        const resizeObserver = new ResizeObserver(() => {
          window.requestAnimationFrame(() => {
            if (isInitialMeasurement) {
              // Skip initial measurement from ResizeObserver, we'll handle it with stability check
              isInitialMeasurement = false;
              return;
            }
            measureWidth();
          });
        });
        resizeObserver.observe(this.node.current);
        this.resizeObserver = resizeObserver;
      }

      // Also watch for layout changes due to CSS class changes or visibility toggles
      if (window.MutationObserver) {
        const mutationObserver = new MutationObserver((mutations) => {
          let shouldMeasure = false;
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' &&
                (mutation.attributeName === 'class' || mutation.attributeName === 'style')) {
              shouldMeasure = true;
            }
          });
          if (shouldMeasure) {
            // Use a small delay to let CSS transitions complete
            setTimeout(measureWidth, 100);
          }
        });

        // Watch the main annotator container for class/style changes
        const annotatorContainer = document.querySelector('.annotator-wrap') || document.body;
        mutationObserver.observe(annotatorContainer, {
          attributes: true,
          attributeFilter: ['class', 'style'],
          subtree: true
        });

        // Also watch the specific annotator contents container that changes with info panel
        const annotatorContents = document.querySelector('.annotator-contents');
        if (annotatorContents) {
          mutationObserver.observe(annotatorContents, {
            attributes: true,
            attributeFilter: ['class', 'style'],
            subtree: false
          });
        }

        // Store cleanup function
        this.mutationObserverCleanup = () => {
          mutationObserver.disconnect();
        };
      }

      // Also listen to window resize as fallback
      let hasInitialMeasurement = false;
      const handleWindowResize = () => {
        if (!hasInitialMeasurement) {
          // Skip window resize events before initial measurement is complete
          return;
        }
        measureWidth();
      };

      window.addEventListener('resize', handleWindowResize);

      // Store the cleanup function
      this.windowResizeCleanup = () => {
        window.removeEventListener('resize', handleWindowResize);
      };

      // Initial measurement - wait for layout stability
      this.waitForStableLayout(() => {
        hasInitialMeasurement = true;
        measureWidth();
      });

      // Immediate measurement for cases where DOM is already stable
      setTimeout(() => {
        if (!hasInitialMeasurement) {
          hasInitialMeasurement = true;
          measureWidth();
        }
      }, 100);
    }
  };

  savedAnnotations = (): Annotation[] => {
    const { annotations } = this.state;
    return annotations.filter((annotation) => annotation.id);
  };

  cancelEditingAnnotation = (): void => {
    const { annotations } = this.state;

    // Revert the other annotations back to their previous content before adding/editing this one
    this.revertAnnotationComments(annotations);

    this.setState({ editingAnnotation: null, postSaveEditRequiredTimestamp: null, annotations }, () => {
      this.addCommentCategoriesClassToHighlights(annotations);
    });
  };

  // eslint-disable-next-line class-methods-use-this
  revertAnnotationComments = (annotations: Annotation[]): void => {
    annotations.forEach((annotation) => {
      /* eslint-disable no-param-reassign */
      if (annotation.previousComment) {
        annotation.comment = annotation.previousComment;
        annotation.previousComment = null;
      }
      if (annotation.previousCommentCategory) {
        annotation.commentCategory = annotation.previousCommentCategory;
        annotation.previousCommentCategory = null;
      }
      /* eslint-enable no-param-reassign */
    });
  };

  editComment = (timestamp: string): void => {
    // Have to wrap actual logic inside the if check because `Annotator.clearCurrentHighlight` is not always available.
    // If it's not available, it will cause error after going through typescript compiler
    if (typeof Annotator.clearCurrentHighlight === 'function') {
      // Clear highlight if one is selected and it's not annotated
      Annotator.clearCurrentHighlight();

      this.setState({ editingAnnotation: timestamp });
    }
  };

  // eslint-disable-next-line class-methods-use-this
  url = (id: string | number): string => {
    let url = Annotator.routes.submissionAnnotations;

    if (id) { url += `/${id}`; }

    return url;
  };

  assignCommentVariants = (fromTimestamp: string, newCommentHelperId: number, callback: () => void): void => {
    const annotations = this.sortedAnnotations();
    const fromAnnotation = annotations.find((a) => a.timestamp === fromTimestamp);

    let postSaveEditRequiredTimestamp = null;

    // Revert the other annotations back to their previous content before editing this one
    this.revertAnnotationComments(annotations);

    // If we're trying to change the annotation comment helper move the various comment variants up/down
    if ((fromAnnotation.commentHelperId || '').toString() !== newCommentHelperId.toString()) {
      this.associateAnnotationToCommentHelper(annotations, fromTimestamp, newCommentHelperId, true);
      postSaveEditRequiredTimestamp = this.deassociateAnnotationFromCommentHelper(annotations, fromAnnotation);
    }

    this.setState({ annotations, postSaveEditRequiredTimestamp }, () => {
      if (callback) callback();
      this.addCommentCategoriesClassToHighlights(annotations);
    });
  };

  // eslint-disable-next-line class-methods-use-this
  associateAnnotationToCommentHelper = (
    annotations: Annotation[],
    fromTimestamp: string,
    newCommentHelperId: number,
    updateFromAnnotation
  ): void => {
    if (!newCommentHelperId) return;
    // Loop over the new comment helper
    // (to assign the current annotation content and move any annotation AFTER the current one down a variant)
    const newCommentHelper = Annotator.data.commentHelpers.find((h) => { // eslint-disable-line arrow-body-style
      return h.id.toString() === newCommentHelperId.toString();
    });
    if (newCommentHelper) {
      let annotationFound = false;

      annotations.filter((annotation) => { // eslint-disable-line arrow-body-style
        return annotation.timestamp === fromTimestamp ||
          (annotation.commentHelperId || '').toString() === newCommentHelperId.toString();
      }).forEach((annotation, index) => {
        if (annotation.timestamp === fromTimestamp) {
          /* eslint-disable no-param-reassign */
          if (newCommentHelper.maximum_annotations > 0) {
            annotationFound = true;
          } else {
            annotation.previousComment = annotation.comment;
            annotation.comment = '';
          }

          annotation.previousCommentCategory = annotation.commentCategory;
          annotation.commentCategory = newCommentHelper ? newCommentHelper.category : CommentCategory.General;
          /* eslint-enable no-param-reassign */
        }

        if (annotationFound && (updateFromAnnotation || annotation.timestamp !== fromTimestamp)) {
          // Save the previous content in case the tutor cancels and we want to roll things back
          /* eslint-disable no-param-reassign */
          annotation.previousComment = annotation.comment;

          annotation.comment = (() => {
            if (index === 0) return newCommentHelper.content;
            if (index === 1) return newCommentHelper.content_2;
            if (index === 2) return newCommentHelper.content_3;
            return '';
          })() || '';
          /* eslint-enable no-param-reassign */
        }
      });
    }
  };

  // eslint-disable-next-line class-methods-use-this
  deassociateAnnotationFromCommentHelper = (annotations: Annotation[], fromAnnotation: Annotation): string => {
    let postSaveEditRequiredTimestamp = null;

    // Loop over the old comment helper (to move any annotation AFTER the current one up a variant)
    const oldCommentHelper = Annotator.data.commentHelpers.find((h) => { // eslint-disable-line arrow-body-style
      return h.id.toString() === (fromAnnotation.commentHelperId || '').toString();
    });
    if (oldCommentHelper && oldCommentHelper.maximum_annotations > 0) {
      let annotationFound = false;
      annotations.filter((annotation) => { // eslint-disable-line arrow-body-style
        return (annotation.commentHelperId || '').toString() === (fromAnnotation.commentHelperId || '').toString();
      }).forEach((annotation, index) => {
        if (annotation.timestamp === fromAnnotation.timestamp) {
          annotationFound = true;
          return; // We want to find the current annotation, but not update it
        }

        if (annotationFound) {
          // Save the previous content in case the tutor cancels and we want to roll things back
          /* eslint-disable no-param-reassign */
          annotation.previousComment = annotation.comment;

          // If an annotation is being promoted to "1st variant" (and it is not an unactioned GED annotation)
          // we need to mark it for edit once the current annotation is saved so it can have the comment
          // updated as per policy
          if (index === 1) {
            postSaveEditRequiredTimestamp = annotation.timestamp;
          }

          annotation.comment = (() => {
            // Indexes are offset by one to account for the current annotation which should not be included
            if (index === 1) return oldCommentHelper.content;
            if (index === 2) return oldCommentHelper.content_2;
            return '';
          })() || '';
          /* eslint-enable no-param-reassign */
        }
      });
    }

    return postSaveEditRequiredTimestamp;
  };

  saveAnnotation = (data: Partial<Annotation>, callback: () => null): void => {
    const { annotations, savingSubmission, postSaveEditRequiredTimestamp } = this.state;
    const newAnnotation = data.id === null;
    const annotationData: AnnotationData = { submission_annotation: data, other_annotations: [] };

    if (savingSubmission) throw new Error('Tried to save annotation, but save already in progress');

    if (newAnnotation) {
      annotationData.submission = {
        lock_version: Annotator.data.lockVersion,
        highlights: Annotator.highlighter.serializeHighlights()
      };
    }

    annotations.forEach((annotation) => {
      if (annotation.previousComment && annotation.id && data.id !== annotation.id) {
        annotationData.other_annotations.push({ id: annotation.id, comment: annotation.comment });
      }
    });

    this.setState({ savingSubmission: true });
    $.ajax({
      type: (newAnnotation ? 'post' : 'put'),
      url: this.url(data.id),
      data: annotationData,
      success: (result) => {
        if (newAnnotation) {
          Annotator.data.lockVersion = result.lock_version;
        }

        const updatedAnnotations = annotations.map((annotation) => {
          /* eslint-disable no-param-reassign */
          annotation.previousComment = null;
          annotation.previousCommentCategory = null;
          /* eslint-enable no-param-reassign */

          if (result.timestamp === annotation.timestamp) {
            return $.extend({}, annotation, {
              id: result.id,
              comment: result.comment,
              commentFlagged: result.comment_flagged,
              commentHelperId: result.comment_helper_id,
              commentHelperIds: [result.comment_helper_id],
              rating: result.rating,
              citeStatus: result.cite_status
            });
          }

          return annotation;
        });

        this.setState({
          savingSubmission: false,
          editingAnnotation: postSaveEditRequiredTimestamp,
          postSaveEditRequiredTimestamp: null,
          annotations: updatedAnnotations
        }, () => {
          if (Annotator.completeAnnotationSave) Annotator.completeAnnotationSave();

          if (postSaveEditRequiredTimestamp) Annotator.scrollTo(postSaveEditRequiredTimestamp);
        });

        if (callback) callback();
      },
      error: (error) => {
        Annotator.handleAjaxError(error, 'Unable to save annotation');
        this.setState({ savingSubmission: false });
      }
    });
  };

  syncPendingCompletions = (): void => {
    this.setState({ pendingCompletions: new Set(this.pendingCompletionToggles) });
  };

  toggleAnnotationCompletion = (annotation: Pick<Annotation, 'timestamp' | 'uuid' | 'completed_at'>): void => {
    if (this.pendingCompletionToggles.has(annotation.timestamp)) return;
    this.pendingCompletionToggles.add(annotation.timestamp);
    this.syncPendingCompletions();

    const nextCompleted = !annotation.completed_at;
    const previousCompletedAt = annotation.completed_at;

    const applyCompletedAt = (completedAt: string | null): void => {
      this.setState((prevState) => ({
        annotations: prevState.annotations.map((a) => (
          a.timestamp === annotation.timestamp ? { ...a, completed_at: completedAt } : a
        ))
      }));
    };

    applyCompletedAt(nextCompleted ? new Date().toISOString() : null);

    fetch(`${Annotator.routes.bellmereSubmissionAnnotations}/${annotation.uuid}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': getCSRFToken()
      },
      body: JSON.stringify({ submission_annotation: { completed: nextCompleted } })
    }).
      then((response) => {
        if (!response.ok) throw new Error('Failed to update annotation completion');

        return response.json();
      }).
      then((data) => applyCompletedAt(data.completed_at)).
      catch(() => {
        applyCompletedAt(previousCompletedAt);
        Annotator.showErrorMessage(
          'Unable to update',
          "We couldn't save that change. Please check your connection and try again."
        );
      }).
      finally(() => {
        this.pendingCompletionToggles.delete(annotation.timestamp);
        this.syncPendingCompletions();
      });
  };

  removeAnnotation = (id: number, timestamp: string, reason: string, errorCallback: () => null): void => {
    const { annotations, savingSubmission, postSaveEditRequiredTimestamp } = this.state;
    let deletedAnnotation: Annotation;

    if (savingSubmission) throw new Error(`Tried to remove annotation ${id}, but save already in progress`);

    const highlightsBeforeDeletion = Annotator.highlighter.serializeHighlights();

    const updatedAnnotations =
      annotations.filter((a) => !a.restorable). // Remove existing undo
        map((a) => {
          if (a.timestamp === timestamp) {
            deletedAnnotation = a;

            if (a.sources) {
              deletedAnnotation.restorable = true;
            } else {
              deletedAnnotation.deleted = true;
            }

            return deletedAnnotation;
          }

          return a;
        }).
        filter((a) => !a.deleted);

    const annotationToDelete = annotations.find((a) => a.timestamp === timestamp);
    if (annotationToDelete?.display_type === DisplayType.Paragraph) {
      const icon = document.getElementById(timestamp) as HTMLElement;
      if (icon) {
        const paragraph = icon.closest('.paragraph-highlight') as HTMLElement;
        const iconContainer = icon.closest('.paragraph-icon-container') as HTMLElement;

        if (iconContainer && iconContainer.children.length <= 1) {
          paragraph.setAttribute('data-comment-category', '');
          paragraph.setAttribute('data-timestamp', '');
        } else {
          const nextIcon = iconContainer.querySelector('.paragraph-icon') as HTMLElement;
          paragraph.setAttribute('data-comment-category', nextIcon.dataset.commentCategory);
          paragraph.setAttribute('data-timestamp', matchTimestamp(nextIcon.id));
        }

        icon.remove();
      }
    } else {
      Annotator.removeHighlight(timestamp);
    }

    const finaliseDelete = () => {
      Annotator.enableHighlighter(true);
      this.setState({
        annotations: updatedAnnotations,
        editingAnnotation: postSaveEditRequiredTimestamp,
        postSaveEditRequiredTimestamp: null,
        savingSubmission: false,
        lastDeleteData: deletedAnnotation?.restorable ? { highlights: highlightsBeforeDeletion, timestamp } : null
      }, Annotator.completeAnnotationSave);
    };

    this.setState({ savingSubmission: true });

    const annotationData = {
      submission: {
        lock_version: Annotator.data.lockVersion,
        highlights: Annotator.highlighter.serializeHighlights()
      },
      submission_annotation: {
        deletion_reason: reason || 'unspecified'
      },
      other_annotations: []
    };

    updatedAnnotations.forEach((annotation) => {
      if (annotation.previousComment && annotation.id && timestamp !== annotation.timestamp) {
        annotationData.other_annotations.push({ id: annotation.id, comment: annotation.comment });
      }
    });

    if (id) {
      $.ajax({
        type: 'delete',
        url: this.url(id),
        data: annotationData,
        success(result) {
          Annotator.data.lockVersion = result.lock_version;

          updatedAnnotations.forEach((annotation) => {
            /* eslint-disable no-param-reassign */
            annotation.previousComment = null;
            annotation.previousCommentCategory = null;
            /* eslint-enable no-param-reassign */
          });

          finaliseDelete();
        },
        error: (error) => {
          Annotator.handleAjaxError(error, 'Unable to remove annotation');
          if (deletedAnnotation) {
            deletedAnnotation.deleted = false;
            deletedAnnotation.restorable = false;
          }
          this.setState({ savingSubmission: false, annotations }, errorCallback);
          Annotator.enableHighlighter(true);
          Annotator.highlighter.removeHighlights();
          Annotator.highlighter.deserializeHighlights(JSON.parse(highlightsBeforeDeletion));
        }
      });
    } else {
      finaliseDelete();
    }
  };

  startRemovingAnnotation = (fromTimestamp: string) => {
    Annotator.enableHighlighter(false);

    const annotations = this.sortedAnnotations();
    const fromAnnotation = annotations.find((a) => a.timestamp === fromTimestamp);

    const postSaveEditRequiredTimestamp = this.deassociateAnnotationFromCommentHelper(annotations, fromAnnotation);

    this.setState({ annotations, postSaveEditRequiredTimestamp });
  };

  cancelRemovingAnnotation = () => {
    Annotator.enableHighlighter(true);

    const annotations = this.sortedAnnotations();
    this.revertAnnotationComments(annotations);
    this.setState({ annotations });
  };

  handleRestoreAnnotation = (annotation: Annotation): void => {
    const { lastDeleteData, savingSubmission } = this.state;
    let restoredAnnotation;
    let restoredWasRestorable = false;

    if (savingSubmission) throw new Error(`Tried to restore annotation ${annotation.id}, but save already in progress`);

    const annotations = this.sortedAnnotations();
    const previousHighlights = lastDeleteData != null ? lastDeleteData.highlights : undefined;
    if (previousHighlights == null) return;

    const updatedAnnotations = annotations.map((a) => {
      if (a.timestamp === annotation.timestamp) {
        restoredAnnotation = a;

        restoredWasRestorable = restoredAnnotation.restorable;
        restoredAnnotation.restorable = false;

        return restoredAnnotation;
      }

      return a;
    });

    const annotationData = {
      submission: {
        lock_version: Annotator.data.lockVersion,
        highlights: previousHighlights
      },
      other_annotations: []
    };

    this.associateAnnotationToCommentHelper(
      updatedAnnotations, annotation.timestamp, annotation.commentHelperId, false
    );

    updatedAnnotations.forEach((other) => {
      if (other.previousComment && other.id && annotation.timestamp !== other.timestamp) {
        annotationData.other_annotations.push({ id: other.id, comment: other.comment });
      }
    });

    this.setState({ savingSubmission: true });

    $.ajax({
      type: 'put',
      url: `${this.url(annotation.id)}/restore`,
      data: annotationData,
      success: (result) => {
        Annotator.data.lockVersion = result.lock_version;

        updatedAnnotations.forEach((a) => {
          /* eslint-disable no-param-reassign */
          a.previousComment = null;
          a.previousCommentCategory = null;
          /* eslint-enable no-param-reassign */
        });

        this.setState({ lastDeleteData: null, savingSubmission: false, annotations: updatedAnnotations }, () => {
          Annotator.highlighter.removeHighlights();
          Annotator.highlighter.deserializeHighlights(JSON.parse(previousHighlights));
          Annotator.completeAnnotationSave();
        });
      },
      error: (error) => {
        Annotator.handleAjaxError(error, 'Unable to restore annotation');
        restoredAnnotation.restorable = restoredWasRestorable;
        this.setState({ savingSubmission: false, annotations });
      }
    });
  };

  openFirstComment = (category: CommentCategory): void => {
    const annotations = this.sortedAnnotations();
    const annotation = annotations.find((a) => resolveCategory(a) === snakeToPascalCase(category));
    const firstAnnotation = annotation || annotations[0];

    if (firstAnnotation) {
      // For multi-paragraph annotations, select only the first paragraph
      const firstParagraphIndex = firstAnnotation.paragraph_indices && firstAnnotation.paragraph_indices.length > 1
        ? firstAnnotation.paragraph_indices[0]?.toString()
        : null;

      // setTimeout ensures that the comment helper is rendered before the annotation is selected
      setTimeout(() => Annotator.selectHighlight(firstAnnotation.timestamp, false, firstParagraphIndex), 0);
    }
  };

  filterHighlightsByCategory = (category: CommentCategory): void => {
    const categoryClass = CategoryClassName[snakeToPascalCase(category)];
    const submissionDocument = document.querySelector('.highlighter-context');
    if (submissionDocument) {
      submissionDocument.setAttribute('data-selected-category', categoryClass);
      this.openFirstComment(category);

      const { annotations } = this.state;
      annotations.forEach((annotation) => {
        if (annotation.display_type === DisplayType.Paragraph) {
          const paragraph =
            document.querySelector(`.paragraph-highlight[data-timestamp='${annotation.timestamp}']`) as HTMLElement;

          if (paragraph) {
            const icons = paragraph.querySelectorAll('.paragraph-icon') as NodeListOf<HTMLElement>;

            if (icons.length > 1) {
              paragraph.dataset.commentCategory = category !== CommentCategory.All ?
                categoryClass :
                icons[0].dataset.commentCategory;
            }
          }
        }
      });
    }
  };

  filterHighlightsByCategories = (categories: CommentCategory[], isOpenFirst = true): void => {
    // Always open the first comment from the last category in the list
    if (isOpenFirst && categories.length > 0) {
      this.openFirstComment(categories.at(-1));
    }

    const categoryClasses = categories.
      map((cat) => CategoryClassName[snakeToPascalCase(cat)]).
      filter(Boolean);

    const submissionDocument = document.querySelector('.highlighter-context');
    if (submissionDocument) {
      // Store selected categories as space-separated classes or as a data attribute
      submissionDocument.setAttribute('data-selected-categories', 'filter-categories');

      const { annotations } = this.state;
      annotations.forEach((annotation) => {
        if (annotation.display_type === DisplayType.Paragraph) {
          const paragraph =
            document.querySelector(`.paragraph-highlight[data-timestamp='${annotation.timestamp}']`) as HTMLElement;

          if (paragraph) {
            const icons = paragraph.querySelectorAll('.paragraph-icon') as NodeListOf<HTMLElement>;

            if (icons.length > 1) {
              // If filtering by multiple categories or non-"All", set to first matching icon
              if (categories.length > 0 && !categories.includes(CommentCategory.All)) {
                const firstMatchingIcon = Array.from(icons).find((icon) => {
                  const iconCategory = icon.dataset.commentCategory;
                  return categoryClasses.some((cls) => iconCategory === cls || iconCategory?.includes(cls));
                });
                paragraph.dataset.commentCategory = firstMatchingIcon?.dataset.commentCategory || categoryClasses[0];
              } else {
                // When "All" is selected, use the first icon's category
                paragraph.dataset.commentCategory = icons[0].dataset.commentCategory;
              }
            }
          }
        }
      });
    }
  };

  // Guard to prevent category/filter changes while in edit mode
  isInEditMode = (): boolean => this.props.isEditable;

  selectCategory = (category: CommentCategory): void => {
    if (this.isInEditMode()) return;
    this.setState({ commentHelperCategory: category });
    this.filterHighlightsByCategory(category);
  };

  commentListFilterChanged = (value: CommentCategory | CommentCategory[]): void => {
    this.setState({ commentHelperCategory: this.getFilterCategory(value) });
  };

  selectCategories = (categories: CommentCategory[], isOpenFirst = true): void => {
    if (this.isInEditMode()) return;
    this.setState({ commentHelperCategory: categories });
    this.filterHighlightsByCategories(categories, isOpenFirst);
  };

  isCommentCategorySelected = (category: CommentCategory): boolean => {
    const { commentHelperCategory } = this.state;

    return commentHelperCategory && [commentHelperCategory].flat().includes(category);
  };

  insertCommentCategory = (category: CommentCategory): void => {
    const { commentHelperCategory, noCommentCategories } = this.state;
    if (noCommentCategories || this.isInEditMode()) return;

    const normalizeCategories = (cat: CommentCategory | CommentCategory[] | null): CommentCategory[] => {
      if (!cat) return [CommentCategory.All];
      return Array.isArray(cat) ? cat : [cat];
    };

    const newFilterCategories = normalizeCategories(commentHelperCategory);

    if (newFilterCategories.includes(CommentCategory.All)) return;

    newFilterCategories.push(category);
    this.selectCategories(newFilterCategories, false);
  };

  handleDisplayCitationsChange = (): void => {
    this.setState((prevState) => ({ displayCitations: !prevState.displayCitations }));
  };

  handleCommentsDeletion = (): void => {
    const { annotations, markedForDeletionTimestamps, savingSubmission } = this.state;

    if (savingSubmission) throw new Error('Tried to delete multiple annotations, but save already in progress');

    const previousHighlights = Annotator.highlighter.serializeHighlights();

    const ids = markedForDeletionTimestamps.map((markedTimestamp) => {
      Annotator.removeHighlight(markedTimestamp);

      const annotation = annotations.find((a) => a.timestamp === markedTimestamp);

      return annotation?.id;
    });
    const updatedAnnotations = annotations.filter((a) => !a.restorable && !this.markedForDeletion(a.timestamp));

    const finaliseDelete = () => {
      this.setState({
        annotations: updatedAnnotations,
        bulkDeleteMode: false,
        confirmingDeletion: false,
        markedForDeletionTimestamps: [],
        lastDeleteData: null,
        savingSubmission: false
      }, Annotator.completeAnnotationSave);
    };

    this.setState({ savingSubmission: true });

    if (ids.length) {
      $.ajax({
        type: 'delete',
        url: this.url('destroy_multiple'),
        data: {
          ids,
          submission: {
            uuid: Annotator.data.submissionUUID,
            lock_version: Annotator.data.lockVersion,
            highlights: Annotator.highlighter.serializeHighlights()
          }
        },
        success(result) {
          Annotator.data.lockVersion = result.lock_version;
          finaliseDelete();
        },
        error: (error) => {
          Annotator.handleAjaxError(error, 'Unable to remove annotations');
          this.setState({ savingSubmission: false });
          Annotator.highlighter.removeHighlights();
          Annotator.highlighter.deserializeHighlights(JSON.parse(previousHighlights));
        }
      });
    } else {
      finaliseDelete();
    }
  };

  handleMarkedForDeletionChange = (timestamp: string) => (): void => {
    const { markedForDeletionTimestamps } = this.state;

    let updated;
    if (this.markedForDeletion(timestamp)) {
      updated = markedForDeletionTimestamps.filter((markedTimestamp) => markedTimestamp !== timestamp);
    } else {
      updated = markedForDeletionTimestamps.concat([timestamp]);
    }

    this.setState({ markedForDeletionTimestamps: updated });
  };

  toggleBulkDeleteMode = (activated: boolean) => (): void => {
    this.setState({ bulkDeleteMode: activated, markedForDeletionTimestamps: [], confirmingDeletion: false });
  };

  handleToggleConfirmDeletion = (confirming: boolean): void => { this.setState({ confirmingDeletion: confirming }); };

  // eslint-disable-next-line class-methods-use-this
  getFilterCategory = (value: CommentCategory | CommentCategory[]): CommentCategory | CommentCategory[] => (
    (Array.isArray(value) ? value : [value]) || [CommentCategory.All]
  );

  annotationVisible = (annotation: Annotation, annotationCategory: CommentCategory): boolean => {
    const { displayCitations, commentHelperCategory } = this.state;

    const isCitation = annotation.sources;
    if (!displayCitations && isCitation) { return false; }

    // no category selected
    if (!commentHelperCategory) { return true; }
    // empty array
    if (Array.isArray(commentHelperCategory) && !commentHelperCategory.length) { return false; }
    // show all
    if (commentHelperCategory.includes('all')) { return true; }
    // general category matched
    if (commentHelperCategory.includes('general') && (annotationCategory === undefined) && !isCitation) { return true; }
    // comment category selected matched
    if (!isCitation && (commentHelperCategory.includes(annotationCategory))) { return true; }
    // bunyip selected matched
    if (commentHelperCategory.includes('use_of_sources') && isCitation) { return true; }
    // no matches return false
    return false;
  };

  orderedAnnotations = (): Annotation[] => {
    const { commentHelperSortType } = this.state;
    const annotations = this.sortedAnnotations();

    if (commentHelperSortType === 'document-order') return annotations;

    // Stable sort keeps document order within each group.
    return annotations.slice().sort(
      (a, b) => Number(matchesTaskRequirement(b)) - Number(matchesTaskRequirement(a))
    );
  };

  navigableAnnotations = (): Annotation[] => this.orderedAnnotations().filter((annotation) => {
    if (annotation.restorable) return false;
    const annotationCategory = annotation.commentCategory?.toLowerCase().replace(/ /g, '_') as CommentCategory;
    return this.annotationVisible(annotation, annotationCategory);
  });

  selectAnnotationByIndex = (index: number): void => {
    const navigable = this.navigableAnnotations();

    if (index < 0 || index >= navigable.length) return;

    const { timestamp, display_type: displayType, paragraph_indices: paragraphIndices } = navigable[index];
    const targetCategory = navigable[index].commentCategory?.toLowerCase().replace(/ /g, '_') as CommentCategory;
    const firstParagraphIndex = paragraphIndices && paragraphIndices.length > 1
      ? paragraphIndices[0]?.toString()
      : null;

    Annotator.selectHighlight(
      displayType === DisplayType.Paragraph ?
        `${timestamp},${CategoryClassName[snakeToPascalCase(targetCategory)]}` : timestamp,
      true,
      firstParagraphIndex
    );
    scrollToHighlight(matchTimestamp(timestamp));
  };

  selectAnnotationByOffset = (offset: number): void => {
    const { selectedAnnotation } = this.state;
    const navigable = this.navigableAnnotations();
    const [currentTimestamp] = (selectedAnnotation || '').split(',');
    const currentIndex = navigable.findIndex((annotation) => annotation.timestamp === currentTimestamp);

    if (currentIndex === -1) return;

    this.selectAnnotationByIndex(currentIndex + offset);
  };

  selectPreviousAnnotation = (): void => this.selectAnnotationByOffset(-1);

  selectNextAnnotation = (): void => this.selectAnnotationByOffset(1);

  sortedAnnotations = (): Annotation[] => {
    const { annotations, highlightOrder } = this.state;
    const { inYambaSession, questionHighlights } = this.props;

    if (inYambaSession && questionHighlights) {
      return annotations.slice(0).sort((annotation1, annotation2) => {
        const annotation1Included = questionHighlights.includes(matchTimestamp(annotation1.timestamp));
        const annotation2Included = questionHighlights.includes(matchTimestamp(annotation2.timestamp));

        if (annotation1Included === annotation2Included) {
          return highlightOrder.indexOf(matchTimestamp(annotation1.timestamp)) -
            highlightOrder.indexOf(matchTimestamp(annotation2.timestamp));
        } if (annotation1Included && !annotation2Included) {
          return -1;
        }
        return 1;
      });
    }

    return annotations.sort((a, b) => highlightOrder.indexOf(matchTimestamp(a.timestamp)) -
      highlightOrder.indexOf(matchTimestamp(b.timestamp)));
  };

  markedForDeletion = (timestamp: string): boolean => {
    const { markedForDeletionTimestamps } = this.state;

    return markedForDeletionTimestamps.includes(timestamp);
  };

  activateTab = (tab: AnnotatorTab): Promise<void> => (
    new Promise((resolve) => { this.setState({ activeTab: tab, commentHelperCategory: null }, resolve); })
  );

  showRequiredNotCitedTextMatchesPanel = (): boolean => {
    const { isEditable, requiredNotCitedPanelContainer } = this.props;

    return isEditable && requiredNotCitedPanelContainer && (this.textMatches().length > 0);
  };

  textMatches = (): Annotation[] => {
    const { annotations } = this.state;

    return annotations.filter((annotation) => (annotation.sources != null) && !annotation.restorable);
  };

  notCitedTextMatchesCount = (): number => (
    this.textMatches().filter((annotation) => annotation.citeStatus === 'not_cited').length
  );

  notCitedAnnotationsNeededCount = (): number => {
    if (!this.notAllCitationsActioned()) { return 0; }

    return Math.max(Annotator.data.minimumNotCitedTextMatchesRequired - this.notCitedTextMatchesCount(), 0);
  };

  notAllCitationsActioned = (): boolean => {
    const { annotations } = this.state;

    const filteredAnnotations =
      annotations.filter((annotation) => (
        !annotation.restorable && annotation.sources && (annotation.citeStatus === 'not_actioned')
      ));

    return filteredAnnotations.length > 0;
  };

  // eslint-disable-next-line class-methods-use-this
  addCommentCategoriesClassToHighlights = (annotations: Annotation[]): void => {
    annotations.forEach((annotation) => {
      const commentCategoriesClass = Annotator.commentCategoriesCSSMap.get(annotation.commentCategory);
      const { highlights } = annotation;
      if (commentCategoriesClass && highlights) {
        $(highlights).attr('data-comment-category', commentCategoriesClass);
      }
    });
  };

  // eslint-disable-next-line class-methods-use-this
  filterCriticalThinkingResults = (criticalThinkingResults: CriticalThinkingResult[]): CriticalThinkingResult[] => {
    if (Annotator.data.criticalThinkingLimit == null) return criticalThinkingResults;

    // Create a map of paragraph index to the number of annotations in that paragraph
    const paragraphAnnotationResultsCount = {};
    Annotator.data.bellmereResults.annotationResults.forEach((result) => {
      if (!paragraphAnnotationResultsCount[result.paragraphIndex]) {
        paragraphAnnotationResultsCount[result.paragraphIndex] = 0;
      }
      paragraphAnnotationResultsCount[result.paragraphIndex] += 1;
    });

    // Sort the critical thinking results by blooms level and then by the number of annotations in the paragraph
    const sortedCriticalThinkingResults = criticalThinkingResults.sort((a, b) => {
      // First, compare by blooms level
      const bloomsLevelComparison = a.bloomsLevelInteger - b.bloomsLevelInteger;
      if (bloomsLevelComparison !== 0) { return bloomsLevelComparison; }

      // If blooms levels are equal, compare by the number of annotations in the paragraph
      const aAnnotationCount = paragraphAnnotationResultsCount[a.paragraphIndex] || 0;
      const bAnnotationCount = paragraphAnnotationResultsCount[b.paragraphIndex] || 0;

      return aAnnotationCount - bAnnotationCount;
    });

    return sortedCriticalThinkingResults.slice(0, Annotator.data.criticalThinkingLimit);
  };

  addBellmereResults = (): void => {
    const { annotations, bellmereResults } = this.state;
    const {
      bellmereRules, persistBellmereResults, userType, isEditable
    } = this.props;
    const documentRoot = Annotator.highlighter.el;

    if (Annotator.data.insertBellmereAnnotations) {
      const originalHighlightHandler = Annotator.highlighter.options.onAfterHighlight;
      let sentences = sentenceLookup(documentRoot);
      let sentencesDirty = false;
      const newAnnotations: Annotation[] = [];
      const appliedResults: AnnotationResult[] = [];
      const paragraphResults: AnnotationResult[] = [];
      Annotator.sortBellmereAnnotationResults(
        bellmereResults.annotationResults,
        bellmereRules,
        sentences.length,
        Annotator.data.wordCount,
        Annotator.data.commentHelperLookup
      ).forEach((result) => {
        if (
          !Annotator.meetsBellmereRules(
            result, appliedResults.concat(paragraphResults), bellmereRules, Annotator.data.commentHelperLookup
          )
        ) return;

        if (result.displayType === DisplayType.Paragraph) {
          paragraphResults.push(result);
          return;
        }

        // If we've modified the sentences then there is a risk they will be dirty so re-calculate them.
        if (sentencesDirty) {
          sentences = sentenceLookup(documentRoot);
          sentencesDirty = false;
        }

        appliedResults.push(result);
        const boundaries = findWordBoundaries(result, sentences, documentRoot);
        if (boundaries) {
          Annotator.highlighter.options.onAfterHighlight = (_range, highlights, timestamp) => {
            if (highlights.length === 0) { return; }

            sentencesDirty = true;
            result.processingDescription = 'annotation_added'; // eslint-disable-line no-param-reassign

            const $highlights = $(documentRoot).find(`.highlighted[data-timestamp="${timestamp}"]`);
            $highlights.first().addClass('first');
            $highlights.last().addClass('last');

            newAnnotations.push({
              comment: result.comment,
              commentHelperIds: result.commentHelperIds,
              commentCategory: capitalize(result.commentHelperCategory),
              correctedSentence: result.awfCorrectedSentence,
              highlightsText: result.annotation,
              timestamp: highlights[0].getAttribute('data-timestamp'),
              commentType: result.commentHelper,
              displayType: DisplayType.Sentence,
              highlights,
              commentHelpers: result.commentHelpers,
              bellmere: true,
              taskAligned: result.taskAligned
            } as unknown as Annotation);

            // Forcibly wait 1ms to ensure we don't get timestamp collisions
            while (new Date().getTime() === timestamp);
          };
          Annotator.highlighter.enable();
          const range = document.createRange();
          range.setStart(boundaries.startElement, boundaries.startOffset);
          range.setEnd(boundaries.endElement, boundaries.endOffset);
          const selection = window.getSelection();
          selection.removeAllRanges();
          selection.addRange(range);

          result.processingDescription = 'existing_annotation_in_place'; // eslint-disable-line no-param-reassign
          Annotator.highlighter.highlightHandler();
          Annotator.highlighter.disable();
        } else {
          result.processingDescription = 'highlight_not_found'; // eslint-disable-line no-param-reassign
        }
      });

      Annotator.highlighter.options.onAfterHighlight = originalHighlightHandler;

      // Grab the highlights now so the submission.highlights don't include the paragraph level annotations
      const highlights = Annotator.highlighter.serializeHighlights();

      // Merge the critical thinking results into the paragraph results
      if (bellmereResults?.criticalThinkingResults) {
        this.filterCriticalThinkingResults(bellmereResults.criticalThinkingResults).forEach((result) => {
          if (result.commentHelper) {
            paragraphResults.push(
              {
                comment: result.feedback,
                commentHelper: result.commentHelper.commentType,
                commentHelperCategory: CommentCategory.CriticalThinking,
                commentHelperId: result.commentHelperId,
                commentHelperIds: [result.commentHelperId],
                displayType: DisplayType.Paragraph,
                paragraphIndex: result.paragraphIndex,
                processingDescription: 'annotation_added',
                sentence: result.paragraph,
                uuid: result.uuid,
                hideCommentType: result.commentHelper.hideCommentType,
                annotation: result.paragraph,
                commentHelpers: [result.commentHelper],
                taskAligned: result.taskAligned
              }
            );
          }
        });
      }

      paragraphResults.forEach((result) => {
        const newAnnotation = addParagraphHighlight(
          result.sentence,
          result.comment,
          result.commentHelperId,
          result.commentHelperIds,
          result.commentHelperCategory,
          result.commentHelper,
          result.uuid,
          result.commentHelpers,
          result.hideCommentType,
          result.taskAligned,
          null,
          result.commentHelper,
          result.paragraphIndices,
          result.sentences
        );
        if (newAnnotation) {
          newAnnotation.bellmere = true;
          newAnnotations.push(newAnnotation);
          result.processingDescription = 'annotation_added'; // eslint-disable-line no-param-reassign
        } else {
          result.processingDescription = 'paragraph_not_found'; // eslint-disable-line no-param-reassign
        }
      });

      const updatedAnnotations = annotations.concat(newAnnotations);
      this.setState({ annotations: updatedAnnotations });

      if (userType === UserTypes.Tutor && isEditable) Annotator.highlighter.enable();

      if (persistBellmereResults) {
        const annotationsForAPI = newAnnotations.map(
          ({
            comment,
            correctedSentence,
            timestamp,
            commentHelperIds,
            display_type,
            highlights_text,
            paragraph_indices: paragraphIndices,
            sentences: annotationSentences
          }) => ({
            awf_corrected_sentence: correctedSentence,
            comment,
            timestamp,
            display_type,
            comment_helper_ids: commentHelperIds,
            highlights: JSON.stringify({ sentence: highlights_text }),
            additional_info: Annotator.getAdditionalInfo(timestamp) || {},
            paragraph_indices: paragraphIndices,
            sentences: annotationSentences
          })
        );

        $.ajax({
          type: 'POST',
          url: Annotator.routes.bellmereSubmissionAnnotations,
          data: {
            submission: {
              bellmere_review_uuid: Annotator.data.bellmereReviewUUID,
              lock_version: Annotator.data.lockVersion,
              highlights
            },
            submission_annotations: annotationsForAPI
          },
          success: (result) => {
            Annotator.data.lockVersion = result.lock_version;
            Annotator.bellmereReady = true;
            this.setState({
              bellmereResults: {
                bellmereResultsProcessedAt: result.bellmere_results_processed_at,
                ...bellmereResults
              },
              annotations: updatedAnnotations.map((annotation) => {
                const persisted = result.submission_annotations.find(
                  ({ timestamp }) => annotation.timestamp === timestamp
                );

                // The response only carries annotations this request persisted, so keep the
                // existing uuid and done state for everything else.
                return {
                  ...annotation,
                  id: persisted?.id,
                  uuid: persisted?.uuid || annotation.uuid,
                  completed_at: persisted ? persisted.completed_at : annotation.completed_at,
                  bellmere: true
                };
              })
            });
          }
        });
        // Set Yamba documentHighlights now we have some annotations
        const yambaStudentApp = document.querySelector<HTMLElement>('.YambaStudentApp');
        if (yambaStudentApp) {
          const yambaProps = JSON.parse(yambaStudentApp.dataset.reactProps);
          yambaStudentApp.dataset.reactProps = JSON.stringify({
            ...yambaProps,
            documentHighlights: updatedAnnotations.map(
              (annotation) => ({
                comment_category: CommentCategory[resolveCategory(annotation)], timestamp: annotation.timestamp
              })
            )
          });
          renderYambaApp();
        }
      }
    }
  };

  // eslint-disable-next-line class-methods-use-this
  getExistingAnnotation = (annotationResults, result) => annotationResults &&
    annotationResults.find((annotationResult) => {
      const { submission_annotation_id } = annotationResult;

      return submission_annotation_id === result.submission_annotation_id;
    });

  // eslint-disable-next-line class-methods-use-this
  getExistingFeedbackSummary = (feedbackSummaries, result) => feedbackSummaries &&
    feedbackSummaries.find((feedbackSummary) => {
      const { feedback_summary_id } = feedbackSummary;

      return feedback_summary_id === result.feedback_summary_id;
    });

  setBellmereQAResult = (result: BellmereQA.AnnotationResult | BellmereQA.FeedbackSummaryResult) => {
    const { bellmereQAResults } = this.state;
    const isAnnotationResult = BellmereQA.isAnnotationResult(result);
    const annotationResults = bellmereQAResults.annotations;
    const feedbackSummaries = bellmereQAResults.feedback_summaries;

    const existingResult = isAnnotationResult ?
      this.getExistingAnnotation(annotationResults, result) :
      this.getExistingFeedbackSummary(feedbackSummaries, result);

    if (existingResult) {
      Object.assign(existingResult, result);
    } else {
      bellmereQAResults[isAnnotationResult ? 'annotations' : 'feedback_summaries'] = [
        ...(bellmereQAResults[isAnnotationResult ? 'annotations' : 'feedback_summaries'] || []), result
      ] as BellmereQA.AnnotationResult[] & BellmereQA.FeedbackSummaryResult[];
    }
    this.saveBellmereQAResults(bellmereQAResults);
  };

  completeBellmereQA = (results: BellmereQA.Results): void => {
    this.saveBellmereQAResults(results, 'complete');
    this.setState({ bellmereQAEditable: false });
  };

  saveBellmereQAResults = (results: BellmereQA.Results, qa_action: string | null = null): void => {
    $.ajax({
      type: 'PATCH',
      url: Annotator.routes.bellmereSubmission,
      data: JSON.stringify({
        submission: {
          bellmere_qa_results: results
        },
        qa_action
      }),
      contentType: 'application/json',
      success: (res) => {
        if (res.qa_queue_path) {
          window.location.href = res.qa_queue_path;
          return;
        }
        this.setState({ bellmereQAResults: res.bellmere_qa_results });
      }
    });
  };

  onSortTypeChange = (sortType: CommentHelperSortType): void => {
    if (this.isInEditMode()) return;
    this.setState({ commentHelperSortType: sortType });
  };

  render(): React.JSX.Element {
    const {
      editingAnnotation,
      savingSubmission,
      lastDeleteData,
      activeTab,
      bulkDeleteMode,
      confirmingDeletion,
      displayCitations,
      markedForDeletionTimestamps,
      selectedAnnotation,
      highlightSelectedAnnotation,
      selectedParagraphIndex,
      width,
      commentHelperCategory,
      bellmereQAResults,
      bellmereQAEditable,
      feedbackAreaCategories,
      pendingCompletions
    } = this.state;

    const {
      isEditable, requiredNotCitedPanelContainer, tutor, feedback, cannedReferrals, client, userType,
      bellmereQAHeaderData, viewingAsBellmereQa, bellmereQAReasons, taskAlertMessages, showTabbedFeedbackLayout,
      inYambaFlow, inYambaSession
    } = this.props;

    let showCitationFilter;
    const annotations = this.sortedAnnotations();
    let visibleCommentCount = 0;
    let visibleCategoryCount = feedbackAreaCategories.length;
    const commentHelpersSummary: CommentHelpersSummary = {};
    const selectedCategories = (() => {
      if (Array.isArray(commentHelperCategory)) {
        return commentHelperCategory as string[];
      }
      if (commentHelperCategory) {
        return [commentHelperCategory as string];
      }
      return null;
    })();

    if (selectedCategories && !selectedCategories.includes(CommentCategory.All)) {
      visibleCategoryCount = [...new Set(selectedCategories)].filter((category) => {
        const feedbackCategory = category === CommentCategory.General ? CommentCategory.All : category;
        return feedbackAreaCategories.includes(feedbackCategory);
      }).length;
    }

    if (isEditable) {
      // Build the summary of comment helpers with first annotation timestamp and total annotations
      annotations.forEach((annotation) => {
        const helperId = (annotation.commentHelperId || '').toString();
        if (!annotation.restorable) {
          if (commentHelpersSummary[helperId]) {
            commentHelpersSummary[helperId].count += 1;
          } else {
            commentHelpersSummary[helperId] = { firstTimestamp: annotation.timestamp, count: 1 };
          }
        }
      });
    }

    const renderAnnotationElements = (_annotations: Annotation[]) => _annotations.map((annotation) => {
      const editing = editingAnnotation === annotation.timestamp;

      let selected: boolean;
      const [timestamp, category] = (selectedAnnotation || '').split(',');
      if (category) {
        selected = timestamp === annotation.timestamp &&
          category === CategoryClassName[snakeToPascalCase(annotation.commentCategory)];
      } else {
        selected = selectedAnnotation === annotation.timestamp;
      }
      const annotationCategory = annotation.commentCategory?.toLowerCase().replace(/ /g, '_') as CommentCategory;
      const visible = this.annotationVisible(annotation, annotationCategory);
      if (visible) { visibleCommentCount += 1; }

      const isEditingDisabled = editingAnnotation && (editingAnnotation !== annotation.timestamp);

      const commentCSSClass = Annotator.commentCategoriesCSSMap.get(annotation.commentCategory);
      let commentClass;
      if (commentCSSClass) { commentClass = `${commentCSSClass}`; }

      const markedForDeletion = this.markedForDeletion(annotation.timestamp);
      const onRestoreAnnotation = () => this.handleRestoreAnnotation(annotation);
      const restorable = annotation.restorable && lastDeleteData && lastDeleteData.highlights;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const Input = typeof (window as any).reduxStore === 'undefined' ? CommentInput : YambaCommentInput;
      const commentInputAnnotation: CommentInputAnnotation = {
        ...annotation,
        isCommentFlagged: annotation.commentFlagged
      };

      const key = [annotation.timestamp, annotation.id].filter(Boolean).join('-');

      return (
        <Fragment key={key}>
          <Input
            annotation={commentInputAnnotation}
            bulkDeleteMode={bulkDeleteMode}
            commentCategory={annotationCategory}
            commentClass={commentClass}
            commentHelpersSummary={commentHelpersSummary}
            confirmingDeletion={confirmingDeletion}
            contextWidth={width}
            isEditable={isEditable}
            isEditing={editing}
            isEditingDisabled={isEditingDisabled}
            isHighlighted={highlightSelectedAnnotation}
            isSelected={selected}
            isVisible={visible}
            markedForDeletion={markedForDeletion}
            onAssignCommentVariants={this.assignCommentVariants}
            onCancelEditingAnnotation={this.cancelEditingAnnotation}
            onCancelRemovingAnnotation={this.cancelRemovingAnnotation}
            onEditComment={this.editComment}
            onMarkedForDeletionChange={this.handleMarkedForDeletionChange(annotation.timestamp)}
            onRemoveAnnotation={this.removeAnnotation}
            onRestoreAnnotation={onRestoreAnnotation}
            onSaveAnnotation={this.saveAnnotation}
            onStartRemovingAnnotation={this.startRemovingAnnotation}
            onToggleAnnotationCompletion={this.toggleAnnotationCompletion}
            completionPending={pendingCompletions.has(annotation.timestamp)}
            restorable={restorable}
            savingSubmission={savingSubmission}
            selectedParagraphIndex={selectedParagraphIndex}
            viewingAsBellmereQa={viewingAsBellmereQa}
            bellmereQAEditable={bellmereQAEditable}
            bellmereQAResults={bellmereQAResults?.annotations}
            setBellmereQAResult={this.setBellmereQAResult}
          />
          {editing && <CommentEditingHelper key="editing-helper" />}
        </Fragment>
      );
    });

    const commentInputs = renderAnnotationElements(this.orderedAnnotations());

    const navigableAnnotations = this.navigableAnnotations();
    const [selectedTimestamp] = (selectedAnnotation || '').split(',');
    const selectedAnnotationIndex = navigableAnnotations.findIndex(
      (annotation) => annotation.timestamp === selectedTimestamp
    );
    const useCommentDeck = commentCarouselEnabled(userType, Annotator.data.product, inYambaSession);
    const showCommentNavigation = useCommentDeck && selectedAnnotationIndex !== -1;
    const commentNavigationBar = (
      <CommentNavigationBar
        currentIndex={selectedAnnotationIndex + 1}
        total={navigableAnnotations.length}
        hasPrevious={selectedAnnotationIndex > 0}
        hasNext={selectedAnnotationIndex < navigableAnnotations.length - 1}
        onPrevious={this.selectPreviousAnnotation}
        onNext={this.selectNextAnnotation}
        onSelectIndex={this.selectAnnotationByIndex}
        panelRef={this.commentListNode}
      />
    );

    if (isEditable || (annotations.length > 0)) {
      showCitationFilter = isEditable && annotations.some((annotation) => annotation.sources);
    }

    // If there are no sources or no visible comments, show the empty state
    const noSources = (annotations.filter((annotation) => !annotation.sources)).length === 0;
    const noVisibleComments = visibleCommentCount === 0;
    const showEmptyState = noSources || noVisibleComments;
    const highlightsCount = annotations.filter((a) => !a.restorable).length;

    const commentListHeaderEmptyState = isEditable && (
      <div className="heading-bar p-2">
        {showEmptyState && <CommentEmptyState />}
        <h5 className="m-0">
          All highlights (
          {highlightsCount}
          )
        </h5>
      </div>
    );

    // Default comment list header
    const commentListHeader = userType === UserTypes.Tutor && !isEditable && (
      <CommentListChipFilter
        commentListFilterChanged={this.commentListFilterChanged}
        annotations={annotations}
        visible={visibleCommentCount}
      />
    );

    // Comment list header portal
    const filterContainer = document.getElementById('filter-portal-container');
    const commentListHeaderPortal = filterContainer && filterContainer.offsetParent && commentListHeader &&
      createPortal(commentListHeader, filterContainer);

    // Comment list header
    const header = commentListHeaderPortal || commentListHeader || commentListHeaderEmptyState || null;

    const commentListContext = { activeTab, selectedAnnotation, highlightSelectedAnnotation };

    const emptyCommentList = commentInputs.every((commentInput) => !commentInput.props.children[0].props.isVisible);

    const renderBellmereQAHeader = (): React.JSX.Element | null => {
      const headerEl = document.getElementById('header');

      return bellmereQAHeaderData && viewingAsBellmereQa && headerEl ?
        createPortal(<QAHeader
          {...bellmereQAHeaderData}
          annotations={annotations}
          feedback={feedback}
          bellmereQAResults={bellmereQAResults}
          rootPath={Annotator.routes.rootPath}
        />, headerEl) :
        null;
    };

    const renderBellmereQAFooter = (): React.JSX.Element | null => {
      const footerEl = document.getElementById('footer');

      return viewingAsBellmereQa && footerEl ?
        createPortal(<QAFooter
          submissionUuid={Annotator.data.submissionUUID}
          qaActionPath={Annotator.routes.qaActionPath}
          qaQueuePath={Annotator.routes.qaQueuePath}
          bellmereQAResults={bellmereQAResults}
          annotations={annotations}
          feedback={feedback}
          reasons={bellmereQAReasons}
          setResults={this.completeBellmereQA}
          readOnly={!bellmereQAEditable}
        />, footerEl) :
        null;
    };

    const renderCommentList = (newLayout = false): React.JSX.Element => (
      <Fragment>
        <div
          ref={this.commentListNode}
          className={['comment-list', newLayout ? 'p-0' : 'p-1', emptyCommentList && 'empty'].filter(Boolean).join(' ')}
        >
          {showCitationFilter && (
            <div className="comment-list-filters">
              <div className="panel-filters">
                {/* Citation filter */}
                {showCitationFilter && (
                  <CitationFilter
                    displayCitations={displayCitations}
                    onChange={this.handleDisplayCitationsChange}
                  />
                )}
              </div>

              {/* Delete mode */}
              {showCitationFilter && !editingAnnotation && (
                <BulkDeletion
                  active={bulkDeleteMode}
                  savingSubmission={savingSubmission}
                  onDelete={this.handleCommentsDeletion}
                  onActivate={this.toggleBulkDeleteMode(true)}
                  onDeactivate={this.toggleBulkDeleteMode(false)}
                  onToggleConfirmDeletion={this.handleToggleConfirmDeletion}
                  confirmingDeletion={confirmingDeletion}
                  selectedCount={markedForDeletionTimestamps.length}
                />
              )}
            </div>
          )}

          {/* (i.e.) All highlights */}
          {header}

          {/* Comment inputs */}
          <div
            role="list"
            className={[
              'comment-view-all d-flex flex-column',
              useCommentDeck && 'comment-view-deck',
              useCommentDeck && selectedAnnotationIndex !== -1 && 'comment-view-deck-engaged'
            ].filter(Boolean).join(' ')}
          >
            {commentInputs}

            {/* Empty state for when there are no comments to show */}
            {(feedback && emptyCommentList && showTabbedFeedbackLayout) &&
              <HighlightEmptyState category={commentHelperCategory} areas={feedback.areas} />}
          </div>

          {/* Outside the deck's scroll/snap region so it stays reachable. */}
          {showCommentNavigation && commentNavigationBar}
        </div>

        {
          this.showRequiredNotCitedTextMatchesPanel() && (
            <RequiredNotCitedPanel
              count={this.notCitedAnnotationsNeededCount()}
              node={requiredNotCitedPanelContainer}
            />
          )
        }
      </Fragment>
    );

    const getBadgeColorClass = () => {
      if (selectedCategories && selectedCategories.length === 1) {
        const category = selectedCategories[0];
        return `comment-category-${category.replace(/_/g, '-')}`;
      }
      return '';
    };

    return (
      <Suspense fallback={<Fragment />}>
        <div
          ref={this.node}
          className={[
            'comment-list-container border-left',
            showTabbedFeedbackLayout && 'tabbed-feedback-layout'
          ].filter(Boolean).join(' ')}
        >
          <CommentListContextProvider value={commentListContext}>
            <TaskAlertMessages messages={taskAlertMessages} />
            {showTabbedFeedbackLayout ? (
              <Fragment>
                <FeedbackCategoryFilter
                  onFilterChange={this.selectCategories}
                  filterHighlightsByCategory={this.filterHighlightsByCategory}
                  feedback={feedback}
                  selectedCategories={selectedCategories as CommentCategory[]}
                  className="position-sticky top-0 st-white-bg"
                  annotations={annotations}
                />
                <SpecialistFeedbackTabs
                  onSortTypeChange={this.onSortTypeChange}
                  lockToHighlightsTab={inYambaFlow}
                  sortOptions={[
                    { value: 'document-order', label: 'Document order' },
                    { value: 'task-requirement', label: 'Task requirement' }
                  ]}
                  showSort={!inYambaFlow}
                  className="position-sticky st-white-bg"
                  title={{
                    summaries: (
                      <span className="cardigan-tab-title d-inline-flex align-items-center gap-1">
                        <span>Summaries</span>
                        <span
                          className={`cardigan-tab-badge ${getBadgeColorClass()}`}
                          data-testid="cardigan-summaries-count"
                        >
                          {visibleCategoryCount}
                        </span>
                      </span>
                    ),
                    highlights: (
                      <span className="cardigan-tab-title d-inline-flex align-items-center gap-1">
                        <span>Highlights</span>
                        <span
                          className={`cardigan-tab-badge ${getBadgeColorClass()}`}
                          data-testid="cardigan-highlights-count"
                        >
                          {visibleCommentCount}
                        </span>
                      </span>
                    )
                  }}
                  content={{
                    summaries: (
                      <CollapsibleSpecialistFeedbackSummaries
                        feedback={feedback}
                        cannedReferrals={cannedReferrals}
                        selectedCategories={selectedCategories}
                        bellmereQAResults={bellmereQAResults?.feedback_summaries}
                        setBellmereQAResult={this.setBellmereQAResult}
                        viewingAsBellmereQa={viewingAsBellmereQa}
                        bellmereQAEditable={bellmereQAEditable}
                        tutor={tutor}
                      />
                    ),
                    highlights: renderCommentList(true)
                  }}
                />
              </Fragment>
            ) : renderCommentList()}

            {/* Bellmere QA Header */}
            {renderBellmereQAHeader()}

            {/* Bellmere QA Footer */}
            {renderBellmereQAFooter()}
          </CommentListContextProvider>
          <SubmissionFeedbackPortal />
          <FlagForQAPortal />
        </div>
      </Suspense>
    );
  }
}

const mapStateToProps = (state) => ({
  questionHighlights: state.questionHighlights,
  inYambaSession: (state.classroomSession.inSession || state.isViewingMeeting),
  inYambaFlow: state.yambaFlow
});

export const YambaCommentList = ReactRedux.connect(mapStateToProps, null, null, { forwardRef: true })(CommentList);

export default CommentList;
